import { post } from 'aws-amplify/api';
import awsExports from '@/aws-exports';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface PatientTokenEmailData {
  to: string;
  patientName: string;
  surgeonName: string;
  tokenUrl: string;
  expiresAt: Date;
  clinicName?: string;
  customMessage?: string;
}

interface LinkRegenerationRequestData {
  patientName: string;
  patientEmail?: string;
  surgeonCognitoId: string;
  surgeonName?: string;
  clinicName?: string;
  patientId?: string;
}

interface EmailResponse {
  success: boolean;
  messageId?: string;
  message?: string;
  error?: string;
  details?: string;
}

class PatientEmailService {
  private static instance: PatientEmailService;

  private apiName: string = 'patientEmailAPI'; // Nom de votre API REST Amplify

  private apiPath: string = '/send-email';

  private constructor() {}

  static getInstance(): PatientEmailService {
    if (!PatientEmailService.instance) {
      PatientEmailService.instance = new PatientEmailService();
    }
    return PatientEmailService.instance;
  }

  /**
   * Envoie un email d'invitation au patient via la Lambda
   */
  async sendPatientFormEmail(emailData: PatientTokenEmailData): Promise<EmailResponse> {
    try {
      const restOperation = post({
        apiName: this.apiName,
        path: this.apiPath,
        options: {
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            emailType: 'patientForm',
            emailData: {
              ...emailData,
              expiresAt: emailData.expiresAt.toISOString(),
            },
          },
        },
      });

      const { body } = await restOperation.response;
      const response = await body.json();

      console.log('Email sent successfully:', response);

      // Sauvegarder l'état de l'envoi
      await this.saveEmailSentStatus(emailData.to, 'patientForm');

      return response as EmailResponse;
    } catch (error: unknown) {
      console.error('Error sending patient form email:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email';
      throw new Error(errorMessage);
    }
  }

  /**
   * Envoie un email de rappel au patient
   */
  async sendReminderEmail(
    emailData: Omit<PatientTokenEmailData, 'expiresAt'>
  ): Promise<EmailResponse> {
    try {
      const restOperation = post({
        apiName: this.apiName,
        path: this.apiPath,
        options: {
          body: {
            emailType: 'reminder',
            emailData,
          },
        },
      });

      const { body } = await restOperation.response;
      const response = await body.json();

      console.log('Reminder email sent successfully:', response);

      // Sauvegarder l'état de l'envoi
      await this.saveEmailSentStatus(emailData.to, 'reminder');

      return response as EmailResponse;
    } catch (error: unknown) {
      console.error('Error sending reminder email:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reminder email';
      throw new Error(errorMessage);
    }
  }

  /**
   * Envoie un email de confirmation après soumission du formulaire
   */
  async sendConfirmationEmail(
    to: string,
    patientName: string,
    surgeonName: string
  ): Promise<EmailResponse> {
    try {
      const restOperation = post({
        apiName: this.apiName,
        path: this.apiPath,
        options: {
          body: {
            emailType: 'confirmation',
            emailData: {
              to,
              patientName,
              surgeonName,
            },
          },
        },
      });

      const { body } = await restOperation.response;
      const response = await body.json();

      console.log('Confirmation email sent successfully:', response);

      // Sauvegarder l'état de l'envoi
      await this.saveEmailSentStatus(to, 'confirmation');

      return response as EmailResponse;
    } catch (error: unknown) {
      console.error('Error sending confirmation email:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to send confirmation email';
      throw new Error(errorMessage);
    }
  }

  /**
   * Envoie une demande de régénération de lien au chirurgien
   * Note: Utilise l'API publique (sans authentification Cognito)
   */
  async sendLinkRegenerationRequestEmail(
    data: LinkRegenerationRequestData
  ): Promise<EmailResponse> {
    try {
      // Utiliser l'API publique sans authentification
      const apiConfig = awsExports.aws_cloud_logic_custom.find(
        (api: { name: string }) => api.name === 'publicPatientAPI'
      );
      if (!apiConfig) {
        throw new Error('API publicPatientAPI not found in configuration');
      }
      const endpoint = apiConfig.endpoint + '/request-link-regeneration';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      console.log('Link regeneration request email sent successfully:', result);

      return result as EmailResponse;
    } catch (error: unknown) {
      console.error('Error sending link regeneration request email:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to send link regeneration request email';
      throw new Error(errorMessage);
    }
  }

  /**
   * Génère l'URL du formulaire patient avec le token
   */
  generatePatientFormUrl(token: string): string {
    // En développement
    if (__DEV__) {
      return `http://localhost:8081/patient/form/${token}`;
    }

    // En production
    return `https://iolmatch-front.netlify.app/patient/form/${token}`;
  }

  /**
   * Sauvegarde l'état d'envoi d'email dans AsyncStorage
   */
  private async saveEmailSentStatus(email: string, type: string): Promise<void> {
    const key = `email_sent_${email}_${type}`;
    const data = {
      sentAt: new Date().toISOString(),
      type,
    };

    try {
      await AsyncStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving email status:', error);
    }
  }

  /**
   * Vérifie si un email a déjà été envoyé
   */
  async checkEmailSentStatus(email: string, type: string): Promise<boolean> {
    const key = `email_sent_${email}_${type}`;

    try {
      const data = await AsyncStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        const sentAt = new Date(parsed.sentAt);
        const hoursSinceSent = (Date.now() - sentAt.getTime()) / (1000 * 60 * 60);

        // Considérer comme non envoyé si plus de 24h
        return hoursSinceSent < 24;
      }
      return false;
    } catch (error) {
      console.error('Error checking email status:', error);
      return false;
    }
  }

  /**
   * Valide une adresse email
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Formate le nom du patient pour l'email
   */
  formatPatientName(prenom: string, nom: string): string {
    return `${prenom} ${nom}`.trim();
  }

  /**
   * Calcule la date d'expiration du token (48h par défaut)
   */
  calculateTokenExpiration(hours: number = 48): Date {
    const expirationDate = new Date();
    expirationDate.setHours(expirationDate.getHours() + hours);
    return expirationDate;
  }
}

export default PatientEmailService.getInstance();
